import { useEffect, useMemo, useCallback, useState } from 'react';
import { Download, FileSpreadsheet } from 'lucide-react';
import { useInvoiceList } from '../hooks/useInvoiceList';
import { invoiceApi } from '../api/invoiceApi';
import { DataList } from '@/Components/shared/List';
import { useListState, useListSearch } from '@/hooks/list';
import { paginationToSkipTake } from '@/utils/list.utils';
import type { ColumnDef, BaseListFilters } from '@/types/list.types';
import { FormInput } from '@/Components/shared/FormInput';
import type { InvoiceListItem } from '../types/invoice.types';
import { InvoiceMobileCards } from './InvoiceMobileCards';
import { getInvoiceStateLabel as getStateLabel, getInvoiceStateColorClass as getStateColorClass } from '../utils/invoiceStateLabels';
import { getInvoiceKindLabel } from '../utils/invoiceKindLabels';

type InvoiceFilters = BaseListFilters;

interface PaymentCounts {
  paid: number;
  pending: number;
}

interface InvoiceListPageProps {
  societyId?: string;
  /** Masquer le header dans le DataList (le parent gère son propre header) */
  hideTitle?: boolean;
  /** Callback pour remonter le total au parent */
  onTotalChange?: (total: number) => void;
  /** Callback pour remonter les counts de paiement au parent */
  onPaymentCountsChange?: (counts: PaymentCounts) => void;
}

function formatDate(dateStr: string | null): string {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleDateString('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
}

function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('fr-FR', {
    style: 'currency',
    currency: 'EUR',
  }).format(amount);
}

// getInvoiceStateLabel / getInvoiceStateColorClass : importés depuis utils
// (partagés avec InvoiceMobileCards pour éviter la redivergence).

export function InvoiceListPage({ societyId, hideTitle = false, onTotalChange, onPaymentCountsChange }: InvoiceListPageProps) {
  const [downloadingPdf, setDownloadingPdf] = useState<number | null>(null);
  const [downloadingDetail, setDownloadingDetail] = useState<number | null>(null);

  const {
    pagination,
    setPagination,
    sortConfig,
    setSortConfig,
    resetFilters,
    activeFiltersCount,
  } = useListState<InvoiceFilters>({
    defaultLimit: 20,
    defaultSortBy: 'createdAt',
    defaultSortDirection: 'DESC',
    persistInUrl: true,
  });

  const { searchTerm, debouncedSearch, setSearchTerm } = useListSearch({
    debounceMs: 300,
    persistInUrl: true,
  });

  const apiFilters = useMemo(() => {
    const { skip, take } = paginationToSkipTake(pagination.page, pagination.limit);
    return {
      skip,
      take,
      search: debouncedSearch || undefined,
      sortBy: sortConfig.sortBy,
      sortDirection: sortConfig.sortDirection,
      societyId,
    };
  }, [pagination, debouncedSearch, sortConfig, societyId]);

  const { data, isLoading, error } = useInvoiceList(apiFilters);

  const invoiceList = useMemo(() => data?.data || [], [data]);
  const total = useMemo(() => data?.total || 0, [data]);

  useEffect(() => { onTotalChange?.(total); }, [total, onTotalChange]);

  const { paidCount, pendingCount } = useMemo(() => {
    if (!invoiceList.length) return { paidCount: 0, pendingCount: 0 };
    return invoiceList.reduce(
      (acc, inv) => {
        if (inv.state === 1) acc.paidCount++;
        else acc.pendingCount++;
        return acc;
      },
      { paidCount: 0, pendingCount: 0 }
    );
  }, [invoiceList]);

  useEffect(() => {
    onPaymentCountsChange?.({ paid: paidCount, pending: pendingCount });
  }, [paidCount, pendingCount, onPaymentCountsChange]);

  const handleDownloadPdf = useCallback(async (invoice: InvoiceListItem) => {
    if (downloadingPdf) return;
    setDownloadingPdf(invoice.id);
    try {
      await invoiceApi.downloadPdf(invoice.id, invoice.label);
    } catch {
      // Silently fail - the user sees the button stop spinning
    } finally {
      setDownloadingPdf(null);
    }
  }, [downloadingPdf]);

  const handleDownloadDetail = useCallback(async (invoice: InvoiceListItem) => {
    if (downloadingDetail) return;
    setDownloadingDetail(invoice.id);
    try {
      if (invoice.isFeesInvoice && invoice.stripeAccountId) {
        await invoiceApi.downloadFeesDetail(invoice.stripeAccountId, invoice.id, invoice.label);
      } else {
        await invoiceApi.downloadDetail(invoice.id, invoice.label);
      }
    } catch {
      // Silently fail
    } finally {
      setDownloadingDetail(null);
    }
  }, [downloadingDetail]);

  const columns: ColumnDef<InvoiceListItem>[] = useMemo(() => ([
    {
      key: 'label',
      sortKey: 'label',
      label: 'Libellé',
      render: (invoice) => (
        <span className="tw-text-sm tw-font-medium">{invoice.label || '-'}</span>
      ),
    },
    {
      key: 'type',
      label: 'Type',
      render: (invoice) => (
        <span className="tw-inline-flex tw-items-center tw-px-2 tw-py-0.5 tw-text-xs tw-font-medium tw-rounded tw-bg-gray-100 tw-text-gray-700">
          {getInvoiceKindLabel(invoice.invoiceKind, 'Contrats & Services')}
        </span>
      ),
    },
    {
      key: 'companyName',
      sortKey: 'companyName',
      label: 'Société',
      render: (invoice) => (
        <span className="tw-text-sm">{invoice.companyName || '-'}</span>
      ),
    },
    {
      key: 'period',
      sortKey: 'startDate',
      label: 'Période',
      render: (invoice) => (
        <span className="tw-text-sm tw-text-gray-500">
          {invoice.startDate && invoice.endDate
            ? `${formatDate(invoice.startDate)} - ${formatDate(invoice.endDate)}`
            : '-'}
        </span>
      ),
    },
    {
      key: 'amountHT',
      sortKey: 'amount',
      label: 'Montant HT',
      render: (invoice) => (
        <span className="tw-text-sm tw-font-medium">
          {formatCurrency(invoice.amount - (invoice.taxAmount || 0))}
        </span>
      ),
    },
    {
      key: 'createdAt',
      sortKey: 'createdAt',
      label: 'Date',
      render: (invoice) => (
        <span className="tw-text-sm tw-text-gray-500">
          {formatDate(invoice.createdAt)}
        </span>
      ),
    },
    {
      key: 'state',
      label: 'État',
      render: (invoice) => (
        <span className={`tw-inline-flex tw-items-center tw-px-2 tw-py-1 tw-text-xs tw-font-medium tw-rounded ${getStateColorClass(invoice.state)}`}>
          {getStateLabel(invoice.state)}
        </span>
      ),
    },
    {
      key: 'actions',
      label: 'Actions',
      render: (invoice) => (
        <div className="tw-flex tw-items-center tw-gap-2">
          {invoice.documentId && (
            <button
              onClick={(e) => { e.stopPropagation(); handleDownloadPdf(invoice); }}
              disabled={downloadingPdf === invoice.id}
              className="tw-inline-flex tw-items-center tw-gap-1 tw-px-2 tw-py-1 tw-text-xs tw-font-medium tw-rounded tw-bg-affinicar-lighter tw-text-affinicar-secondary hover:tw-bg-affinicar-light tw-transition-colors disabled:tw-opacity-50"
              title="Télécharger la facture PDF"
            >
              <Download className={`tw-w-3.5 tw-h-3.5 ${downloadingPdf === invoice.id ? 'tw-animate-pulse' : ''}`} />
              PDF
            </button>
          )}
          <button
            onClick={(e) => { e.stopPropagation(); handleDownloadDetail(invoice); }}
            disabled={downloadingDetail === invoice.id}
            className="tw-inline-flex tw-items-center tw-gap-1 tw-px-2 tw-py-1 tw-text-xs tw-font-medium tw-rounded tw-bg-gray-100 tw-text-gray-700 hover:tw-bg-gray-200 tw-transition-colors disabled:tw-opacity-50"
            title={invoice.isFeesInvoice ? 'Télécharger le rapprochement comptable (CSV)' : 'Télécharger le détail des contrats (CSV)'}
          >
            <FileSpreadsheet className={`tw-w-3.5 tw-h-3.5 ${downloadingDetail === invoice.id ? 'tw-animate-pulse' : ''}`} />
            Détail
          </button>
        </div>
      ),
    },
  ] as ColumnDef<InvoiceListItem>[]), [handleDownloadPdf, handleDownloadDetail, downloadingPdf, downloadingDetail]);

  return (
    <DataList
      data={invoiceList}
      total={total}
      isLoading={isLoading}
      error={error as Error}
      embedded
      customHeaderLeft={hideTitle ? undefined : (
        <div className="tw-flex tw-items-center tw-gap-3">
          {paidCount > 0 && (
            <span className="tw-px-2.5 tw-py-1 tw-text-xs tw-font-medium tw-rounded-full tw-bg-green-100 tw-text-green-700">
              {paidCount} payée{paidCount > 1 ? 's' : ''}
            </span>
          )}
          {pendingCount > 0 && (
            <span className="tw-px-2.5 tw-py-1 tw-text-xs tw-font-medium tw-rounded-full tw-bg-orange-100 tw-text-orange-700">
              {pendingCount} non payée{pendingCount > 1 ? 's' : ''}
            </span>
          )}
        </div>
      )}
      columns={columns}
      filtersClassName="tw-mt-4"
      sortConfig={sortConfig}
      onSortChange={setSortConfig}
      filters={
        <div className="tw-flex tw-flex-wrap tw-gap-3 tw-items-end">
          <div className="tw-flex tw-flex-col tw-gap-1 tw-w-full md:tw-w-64">
            <span className="tw-text-xs tw-text-gray-400 tw-font-normal">Recherche</span>
            <FormInput
              type="text"
              placeholder="Rechercher..."
              value={searchTerm}
              onChange={(value) => setSearchTerm(value || '')}
              size="sm"
            />
          </div>
        </div>
      }
      activeFiltersCount={activeFiltersCount + (debouncedSearch ? 1 : 0)}
      onResetFilters={() => { resetFilters(); setSearchTerm(''); }}
      pagination={pagination}
      onPaginationChange={setPagination}
      getRowKey={(invoice) => invoice.id}
      mobileCardView={InvoiceMobileCards}
      emptyMessage="Aucune facture trouvée"
    />
  );
}
